/**
 * Is a new account ready for its first campaign?
 *
 * Connecting Follow Up Boss only stores the key. The rest happens in the
 * background after payment: Mission Control provisions the organization's
 * database, imports people page by page (FUB rate limits make this minutes
 * to hours), and someone switches campaign engine v2 on. No single flag says
 * "done", so readiness is read from the signals that do exist:
 *
 *   - organization status (`RUNNING` only arrives at the very end),
 *   - the onboarding task list (`/onboarding-progress`, production only),
 *   - whether lead sources / tags have arrived (empty until the import pages
 *     through people; empty answers come back 200, so "none yet" and "none"
 *     look the same),
 *   - the campaign engine's own readiness (`campaign-config-v2`).
 *
 * Every signal is `null` when its request failed. Unknown never blocks: the
 * gate only holds a person back on a signal that positively says "not yet",
 * so a backend hiccup can never lock an existing customer out.
 */

export type TStepState = 'done' | 'working' | 'waiting' | 'blocked' | 'info'

export interface IProgressCount {
   phase: string
   unit: string
   completed: number
   total: number | null
}

export interface IProgressStep {
   operation: string
   status: string
   progress: IProgressCount | null
   blocker: string | null
}

export interface IOnboardingProgress {
   status: string
   steps: IProgressStep[]
}

export interface ICampaignEngine {
   enabled: boolean
   ready: boolean
   reasons: string[]
}

export interface IReadinessSignals {
   orgStatus: string | null
   onboardingStatus: string | null
   leadSourceCount: number | null
   tagCount: number | null
   engine: ICampaignEngine | null
   progress: IOnboardingProgress | null
}

export interface ITrackerStep {
   id: 'account' | 'leads' | 'calling' | 'texting'
   title: string
   detail: string
   state: TStepState
}

export interface IReadiness {
   /** The campaign flow can open. */
   canBuild: boolean
   /** Leads have arrived (or something says the import finished). */
   leadsReady: boolean | null
   /** Campaign engine v2 is on and ready. */
   engineReady: boolean | null
   steps: ITrackerStep[]
}

const stepOf = (p: IOnboardingProgress | null, operation: string) =>
   p?.steps.find((s) => s.operation === operation) || null

const isDone = (s: IProgressStep | null) =>
   Boolean(s && (s.status === 'completed' || s.status === 'skipped'))

const isBlocked = (s: IProgressStep | null) =>
   Boolean(s && ['blocked', 'failed', 'outcome_unknown'].includes(s.status))

const plural = (n: number, word: string) =>
   `${n.toLocaleString('en-US')} ${word}${n === 1 ? '' : 's'}`

/** "1,240 of 3,000 contacts" / "1,240 contacts so far". */
export const progressLine = (p: IProgressCount | null): string | null => {
   if (!p) return null
   const unit = p.unit === 'contacts' ? 'contact' : p.unit.replace(/s$/, '')
   if (p.total !== null && p.total > 0)
      return `${p.completed.toLocaleString('en-US')} of ${plural(p.total, unit)}`
   return p.completed > 0 ? `${plural(p.completed, unit)} so far` : null
}

const ENGINE_REASONS: Record<string, string> = {
   campaign_config_v2_disabled:
      'Our team switches campaigns on for your account.',
   task_mode_required: 'Finishing the calling setup.',
   experimental_scoring_required: 'Finishing the calling setup.',
}

/** The engine's reasons in plain words, each said once. */
export const engineDetail = (engine: ICampaignEngine | null): string => {
   if (!engine) return 'Checking the calling setup.'
   if (engine.enabled && engine.ready) return 'Calling is set up.'
   const lines = Array.from(
      new Set(
         engine.reasons.map(
            (r) => ENGINE_REASONS[r] || 'Finishing the calling setup.'
         )
      )
   )
   return lines.length ? lines.join(' ') : 'Finishing the calling setup.'
}

export const deriveReadiness = (s: IReadinessSignals): IReadiness => {
   const running = s.orgStatus === 'RUNNING'
   const onboarded = s.onboardingStatus === 'completed'

   const importStep = stepOf(s.progress, 'crm.import')
   const counted =
      s.leadSourceCount === null && s.tagCount === null
         ? null
         : (s.leadSourceCount || 0) > 0 || (s.tagCount || 0) > 0
   const leadsReady: boolean | null =
      running || onboarded || isDone(importStep)
         ? true
         : counted === true
           ? true
           : counted === false || (importStep && !isDone(importStep))
             ? false
             : null

   const engineReady: boolean | null = s.engine
      ? s.engine.enabled && s.engine.ready
      : null

   // Unknown never blocks; only a known "not yet" does.
   const canBuild = running || (leadsReady !== false && engineReady !== false)

   const leadsState: TStepState = leadsReady
      ? 'done'
      : isBlocked(importStep)
        ? 'blocked'
        : importStep?.status === 'in_progress' || counted === false
          ? 'working'
          : 'waiting'
   const leadsDetail = leadsReady
      ? 'Your leads are in.'
      : leadsState === 'blocked'
        ? 'The import paused. Our team has been told and will restart it.'
        : progressLine(importStep?.progress || null) ||
          'Copying your Follow Up Boss leads. Big accounts take a few hours.'

   const engineSteps = ['deployment.configure', 'campaign.provision']
      .map((o) => stepOf(s.progress, o))
      .filter(Boolean) as IProgressStep[]
   const callingState: TStepState = engineReady
      ? 'done'
      : engineSteps.some(isBlocked)
        ? 'blocked'
        : engineReady === false
          ? 'working'
          : 'waiting'

   const a2p = stepOf(s.progress, 'a2p.register')
   const textingState: TStepState = isDone(a2p)
      ? 'done'
      : isBlocked(a2p)
        ? 'blocked'
        : 'info'

   return {
      canBuild,
      leadsReady,
      engineReady,
      steps: [
         {
            id: 'account',
            title: 'Account and Follow Up Boss',
            detail: 'Paid and connected.',
            state: 'done',
         },
         {
            id: 'leads',
            title: 'Importing your leads',
            detail: leadsDetail,
            state: leadsState,
         },
         {
            id: 'calling',
            title: 'Calling setup',
            detail:
               callingState === 'blocked'
                  ? 'Waiting on our team to finish your numbers.'
                  : engineDetail(s.engine),
            state: callingState,
         },
         {
            id: 'texting',
            title: 'Texting approval',
            detail:
               textingState === 'done'
                  ? 'Carriers approved your business.'
                  : textingState === 'blocked'
                    ? 'The carriers need something from you. Check your email.'
                    : 'Carriers take 3 to 5 days. Calls do not wait for it.',
            state: textingState,
         },
      ],
   }
}

/** Raw `/onboarding-progress` JSON → the parts the tracker reads. */
export const parseProgress = (raw: unknown): IOnboardingProgress | null => {
   const d = raw as { status?: unknown; steps?: unknown } | null
   if (!d || typeof d !== 'object' || !Array.isArray(d.steps)) return null
   const steps = (d.steps as Record<string, unknown>[])
      .filter((x) => x && typeof x.operation === 'string')
      .map((x): IProgressStep => {
         const p = x.progress as Record<string, unknown> | null
         const completed = Number(p?.completed)
         const total = p?.total === null ? null : Number(p?.total)
         return {
            operation: String(x.operation),
            status: String(x.status || 'unknown'),
            blocker: x.blocker ? String(x.blocker) : null,
            progress:
               p && Number.isFinite(completed)
                  ? {
                       phase: String(p.phase || ''),
                       unit: String(p.unit || 'contacts'),
                       completed,
                       total:
                          total !== null && Number.isFinite(total) ? total : null,
                    }
                  : null,
         }
      })
   return { status: String(d.status || 'unknown'), steps }
}
